import { useState, useEffect } from "react";
import { callApi } from "../../../utils/api/api";
import constructOptimisedImageUrl from "../../../utils/imageOptimisation/constructOptimisedImageUrl";
import ExtraPopup from "../../../components/extraPopup";
import { getUserCurrencyLabel, getUserCurrencySymbol } from "../../../utils/userCurrencyUtils";
import { UserModel } from "../../../interfaces/models/userModel";
import {
    ViewListingExtrasWrapper,
    ViewListingExtrasTitle,
    ViewListingExtrasContainer,
    ViewListingExtraItem,
    ViewListingExtraImageContainer,
    ViewListingExtraImage,
    ViewListingExtraAddButton,
    ViewListingExtraContent,
    ViewListingExtraTitle,
    ViewListingExtraDescription,
    ViewListingExtraPrice,
} from "../viewListingStyling";

interface ExtraImage {
    key: string;
    alt: string;
    height: number | null;
    width: number | null;
}

interface Extra {
    id: string;
    draft: boolean;
    status: string;
    extraImages: ExtraImage[];
    name: string;
    description?: string;
    price: number;
    productType?: string;
    selectedLocations?: string[];
    userhostmodelID: string;
    productTitle?: string;
    productDescription?: string;
    netPrice?: number;
    productImages?: string[];
    productFulfillmentType?: string;
    pricingType?: string;
    inventory?: number;
    location?: string;
    fulfillmentRadius?: number;
    guestOnlyPurchase?: boolean;
    minOrderQuantity?: number;
    maxOrderQuantity?: number;
    minOrderAmount?: number;
    maxOrderAmount?: number;
    maxGuests?: number;
    duration?: number;
    minimumNotice?: number;
    unavailableDates?: string[];
    internalCode?: string;
    unlimitedInventory?: boolean;
    approved?: boolean;
    instantBookEnabled?: boolean;
    categories?: string[];
    tags?: string[];
    createdAt?: string;
    updatedAt?: string;
}

interface ViewListingExtrasProps {
    listingId: string;
    accessToken: string;
    userModel: UserModel | null;
}

export default function ViewListingExtras({ listingId, accessToken, userModel }: ViewListingExtrasProps) {
    const [extras, setExtras] = useState<Extra[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedExtra, setSelectedExtra] = useState<Extra | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const currencyLabel = getUserCurrencyLabel(userModel);
    const currencySymbol = getUserCurrencySymbol(userModel);

    useEffect(() => {
        const fetchExtras = async () => {
            if (!listingId || !accessToken) {
                setLoading(false);
                return;
            }

            try {
                // Fetch products linked to this listing using the getListingProducts endpoint
                const response = await callApi("get", `${process.env.REACT_APP_ENV}-anystay-extras`, `listing/${listingId}/products`, {
                    headers: {
                        Authorization: accessToken,
                    },
                });

                if (response?.success && response.body?.products) {
                    // Transform the API response to match the expected Extra interface
                    const transformedExtras = response.body.products
                        .filter((product: any) => {
                            // Check if the product is active and not draft
                            if (!product || product.status !== 'active' || product.draft) {
                                return false;
                            }
                            return true;
                        })
                        .map((product: any) => {
                            return {
                                id: product.id,
                                draft: product.draft || false,
                                status: product.status || 'active',
                                extraImages: product.productImages ? product.productImages.map((imgStr: string) => {
                                    try {
                                        const parsed = JSON.parse(imgStr);
                                        return {
                                            key: parsed.key,
                                            alt: parsed.alt || '',
                                            height: parsed.height,
                                            width: parsed.width
                                        };
                                    } catch {
                                        return { key: '', alt: '', height: null, width: null };
                                    }
                                }) : [],
                                name: product.productTitle || '',
                                description: product.productDescription || '',
                                price: (product.netPrice || 0) / 100, // Convert from cents to dollars
                                productType: product.productType || '',
                                userhostmodelID: product.userhostmodelID || '',
                                productTitle: product.productTitle,
                                productDescription: product.productDescription,
                                netPrice: product.netPrice,
                                productImages: product.productImages,
                                productFulfillmentType: product.productFulfillmentType,
                                pricingType: product.pricingType,
                                inventory: product.inventory,
                                location: product.location,
                                fulfillmentRadius: product.fulfillmentRadius,
                                guestOnlyPurchase: product.guestOnlyPurchase,
                                minOrderQuantity: product.minOrderQuantity,
                                maxOrderQuantity: product.maxOrderQuantity,
                                minOrderAmount: product.minOrderAmount,
                                maxOrderAmount: product.maxOrderAmount,
                                maxGuests: product.maxGuests,
                                duration: product.duration,
                                minimumNotice: product.minimumNotice,
                                unavailableDates: product.unavailableDates,
                                internalCode: product.internalCode,
                                unlimitedInventory: product.unlimitedInventory,
                                approved: product.approved,
                                instantBookEnabled: product.instantBookEnabled,
                                categories: product.categories,
                                tags: product.tags,
                                createdAt: product.createdAt,
                                updatedAt: product.updatedAt,
                            };
                        });
                    
                    setExtras(transformedExtras);
                }
            } catch (error) {
                console.error("Error fetching extras:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchExtras();
    }, [listingId, accessToken]);

    if (loading) {
        return null; // Don't show anything while loading
    }

    const handleExtraClick = (extra: Extra) => {
        setSelectedExtra(extra);
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        setIsPopupOpen(false);
        setSelectedExtra(null);
    };

    const handleOrder = () => {
        // Handle order logic here
        console.log('Ordering extra:', selectedExtra?.name);
        // You can add navigation to booking flow or other order handling
    };

    if (extras.length === 0) {
        return null; // Don't show the section if there are no extras
    }

    return (
        <>
            <ViewListingExtrasWrapper>
                <ViewListingExtrasTitle>Add extras to your stay</ViewListingExtrasTitle>
                <ViewListingExtrasContainer>
                    {extras.map((extra) => (
                        <ViewListingExtraItem 
                            key={extra.id}
                            onClick={() => handleExtraClick(extra)}
                        >
                            <ViewListingExtraImageContainer>
                                {extra.extraImages.length > 0 && (
                                    <ViewListingExtraImage
                                        src={constructOptimisedImageUrl(extra.extraImages[0].key, 200)}
                                        alt={extra.name}
                                    />
                                )}
                                <ViewListingExtraAddButton 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleExtraClick(extra);
                                    }}
                                    aria-label={`Add ${extra.name} to your stay`}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M8 3V13" stroke="#222222" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M13 8L3 8" stroke="#222222" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                </ViewListingExtraAddButton>
                            </ViewListingExtraImageContainer>
                            <ViewListingExtraContent>
                                <ViewListingExtraTitle>{extra.name}</ViewListingExtraTitle>
                                {extra.description && (
                                    <ViewListingExtraDescription>{extra.description}</ViewListingExtraDescription>
                                )}
                                <ViewListingExtraPrice>{currencySymbol}{extra.price.toFixed(2)} {currencyLabel}</ViewListingExtraPrice>
                            </ViewListingExtraContent>
                        </ViewListingExtraItem>
                    ))}
                </ViewListingExtrasContainer>
            </ViewListingExtrasWrapper>

            {selectedExtra && (
                <ExtraPopup
                    isOpen={isPopupOpen}
                    onClose={handleClosePopup}
                    extraData={selectedExtra}
                    onOrder={handleOrder}
                />
            )}
        </>
    );
} 